import { Component, DestroyRef, ElementRef, HostListener } from '@angular/core';
import { ViewEncapsulation } from '@angular/core';
import { AuthService } from '@Services/auth.service';
import { Router, Event, NavigationStart, NavigationEnd, NavigationCancel, NavigationError, Navigation, NavigationExtras } from '@angular/router';
import { LoadingService } from '@Services/loading.service';
import { DragDropService } from '@Services/drag-drop.service';
import { AlertsHub } from '@Core/hubs/alerts.hub';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { ClearanceHub } from '@Core/hubs/clearance.hub';
import { FeatureFacetService } from '@Core/services/feature-facet.service';
import { TenantPermissions } from '@Core/enums/codegen/tenant-permissions.enum';
import { Store } from '@ngrx/store';
import { Store as NgxsStore } from '@ngxs/store';
import { getOpenPolicies } from '@Core/states/policies/policies-selectors';
import { OpenPolicy } from '@Core/models/openPolicy';
import _ from 'lodash';
import { SyncHubStatusService } from '@Core/services/sync-hub-status.service';
import { SyncStatus } from '@Core/enums/sync-status.enum';
import { OpenProprietaryPoliciesState } from '@Core/models/proprietary/page/open-proprietary-policies.state';

@Component({
    standalone: false,
    selector: 'work',
    templateUrl: './work.component.html',
    styleUrls: ['./work.component.scss'],
    encapsulation: ViewEncapsulation.None
})

export class WorkComponent {
    public loading = false;
    public TENANT_PERMISSIONS = TenantPermissions;

    public openPolicies: OpenPolicy[] = [];
    public isMainMenuOpen = false;

    // Consumed by the non-Aurora header only; the Aurora header sources this from AuroraHeaderDirective.
    public syncStatus: SyncStatus;

    constructor(public authService: AuthService,
        private router: Router,
        private loadingService: LoadingService,
        private elementRef: ElementRef,
        private dragDropService: DragDropService,
        private featureFacetService: FeatureFacetService,
        private syncStatusService: SyncHubStatusService,
        private alertsHub: AlertsHub,
        private clearanceHub: ClearanceHub,
        private destroyRef: DestroyRef,
        private store: Store,
        private ngxsStore: NgxsStore) {

        this.authService.spaName = 'work';
        this.loadingService.loading
            .pipe(takeUntilDestroyed(destroyRef))
            .subscribe(l => {
                this.loading = l;
            });

        this.router.events
            .pipe(takeUntilDestroyed(this.destroyRef))
            .subscribe((event: Event) => {

                const extras: NavigationExtras = this.router.getCurrentNavigation()?.extras;
                const shouldSkipLoading = extras?.info?.["skipLoading"] ?? false;

                if (shouldSkipLoading)
                    return;

                switch (true) {
                    case event instanceof NavigationStart: {
                        this.loadingService.loading.next(true);
                        break;
                    }

                    case event instanceof NavigationEnd:
                    case event instanceof NavigationCancel:
                    case event instanceof NavigationError: {
                        this.loadingService.loading.next(false);
                        break;
                    }
                    default: {
                        break;
                    }
                }
            });
    }

    ngOnInit() {
        this.alertsHub.startConnection();

        this.featureFacetService.getAllFacets().subscribe(allFacets => {
            if (FeatureFacetService.checkIfFacetExists(TenantPermissions.Flag_Base_Work_Accounts_AccountClearance,
                allFacets, 'work', this.authService.getAreaKey())) {
                this.clearanceHub.startConnection();
            }
            if (FeatureFacetService.checkIfFacetExists(TenantPermissions.Flag_Base_Work_Accounts_BureauMode,
                allFacets, 'work', this.authService.getAreaKey())) {
                this.store.select(getOpenPolicies)
                    .pipe(takeUntilDestroyed(this.destroyRef))
                    .subscribe(openPolicies => {
                        this.openPolicies = openPolicies
                    });
            }
        });

        this.syncStatusService.status
            .pipe(takeUntilDestroyed(this.destroyRef))
            .subscribe((status: string) => {
                this.syncStatus = <SyncStatus>status;
            });
    }

    public toggleMainMenu(event: MouseEvent): void {
        event.stopPropagation();
        this.isMainMenuOpen = !this.isMainMenuOpen;
    }

    @HostListener('document:click')
    public closeMainMenu(): void {
        this.isMainMenuOpen = false;
    }

    @HostListener('window:beforeunload', ['$event'])
    unloadNotification($event: any): void {
        // If any open bureau policies with unsaved changes, make the user confirm
        if (_.some(this.openPolicies, op => op.serverPolicy?.docStatus?.DocumentModified)) {
            $event.preventDefault();
            $event.returnValue = true;
            return $event;
        }
        // If any open proprietary policies with unsaved changes
        const hasProprietaryDirty = this.ngxsStore.selectSnapshot(
            OpenProprietaryPoliciesState.isAnyDirty
        );
        if (hasProprietaryDirty) {
            $event.preventDefault();
            $event.returnValue = true;
            return $event;
        }
    }

    onDragOver(event: DragEvent): void {
        event.stopPropagation();
        event.preventDefault();
        this.dragDropService.setDragging(true);
    }

    onDragLeave(event: DragEvent): void {
        if (!event.relatedTarget || !this.elementRef.nativeElement.contains(event.relatedTarget)) {
            this.dragDropService.setDragging(false);
        }
    }
};
